import React, { useContext } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, Col, Row } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCashRegister,
  faCheckCircle,
  faClock,
  faFileInvoiceDollar,
  faUniversity,
} from "@fortawesome/free-solid-svg-icons";
import { ChevronRight, HandCoins, Landmark, PiggyBank, ReceiptText } from "lucide-react";
import Loader from "../../common/Loader";
import { CurrencyContext } from "../../../context/Context";
import Money, { BalanceAmount, ConvertedMoney } from "./Money";
import CondominiumMovements from "./CondominiumMovements";
import { sectionHref } from "./sections";

const sumBalances = (accounts) =>
  accounts.reduce((sum, account) => sum + parseFloat(account.balance || 0), 0);

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

// Saldo disponible de cajas y bancos: tarjeta blanca con el brillo de la
// marca, el monto grande y, para el administrador, los atajos más usados.
const AvailableHero = ({ value, loading, isManager, base }) => {
  const { currency: curr } = useContext(CurrencyContext);
  const rate = Number(curr?.value || 1);

  return (
    <Card className="hl-balance mb-3">
      <span className="hl-caption">Disponible hoy · cajas y bancos</span>
      {loading ? (
        <div className="hl-balance__amount text-400">…</div>
      ) : (
        <BalanceAmount value={value} className="hl-balance__amount" />
      )}
      {!loading && curr?.currency?.iso && rate !== 1 && (
        <div className="fs--1 font-weight-medium text-600">
          ≈ <ConvertedMoney value={value} />
        </div>
      )}
      {isManager && (
        <div className="hl-balance__actions">
          <Button tag={Link} to={sectionHref("collections")} color="primary">
            <HandCoins size={16} strokeWidth={2} className="mr-2" aria-hidden="true" />
            Cobros
          </Button>
          <Button tag={Link} to={sectionHref("expenses")} color="falcon-default">
            <ReceiptText size={16} strokeWidth={2} className="mr-2" aria-hidden="true" />
            Gastos
          </Button>
          <Button tag={Link} to="/dashboard/banks" color="falcon-default">
            <Landmark size={16} strokeWidth={2} className="mr-2" aria-hidden="true" />
            Bancos
          </Button>
          <Button tag={Link} to={`${base}/reserve-funds`} color="falcon-default">
            <PiggyBank size={16} strokeWidth={2} className="mr-2" aria-hidden="true" />
            Fondos
          </Button>
        </div>
      )}
    </Card>
  );
};

AvailableHero.propTypes = {
  value: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  isManager: PropTypes.bool,
  base: PropTypes.string,
};

// Cifra de cabecera: cuadro con icono, mancha de la marca en la esquina, el
// monto y el enlace a su detalle. Con `to` toda la tarjeta es clicable.
export const StatCard = ({ icon: Icon, color, label, caption, value, valueClass, to, linkLabel }) => (
  <Card className={`hl-feature-card hl-feature-card--${color} h-100 ${to ? "hover-shadow" : ""}`}>
    <CardBody className="d-flex flex-column">
      <span className={`hl-icon-tile hl-icon-tile--${color}`} aria-hidden="true">
        <Icon size={20} strokeWidth={1.75} />
      </span>
      <h3 className="hl-feature-card__title mb-0">{label}</h3>
      <p className="hl-feature-card__text">{caption}</p>
      <p className={`hl-feature-card__value ${valueClass || ""}`}>{value}</p>
      {to && (
        <Link to={to} className="hl-feature-card__link stretched-link mt-auto pt-2">
          {linkLabel}
          <ChevronRight size={14} strokeWidth={2} className="ml-1" aria-hidden="true" />
        </Link>
      )}
    </CardBody>
  </Card>
);

StatCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  color: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  caption: PropTypes.node,
  value: PropTypes.node.isRequired,
  valueClass: PropTypes.string,
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  linkLabel: PropTypes.string,
};

const AttentionItem = ({ icon, color, title, detail, to, actionLabel, primary }) => (
  <div className="hl-row hl-row--muted mb-2">
    <span className={`hl-icon-tile hl-icon-tile--sm hl-icon-tile--${color}`} aria-hidden="true">
      <FontAwesomeIcon icon={icon} />
    </span>
    <div className="hl-row__body">
      <h6 className="hl-row__title">{title}</h6>
      {detail && <p className="hl-row__meta mb-0">{detail}</p>}
    </div>
    <Button
      tag={Link}
      to={to}
      size="sm"
      color={primary ? "primary" : "falcon-default"}
      className="flex-shrink-0"
    >
      {actionLabel}
    </Button>
  </div>
);

AttentionItem.propTypes = {
  icon: PropTypes.object.isRequired,
  color: PropTypes.string.isRequired,
  title: PropTypes.node.isRequired,
  detail: PropTypes.node,
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  actionLabel: PropTypes.string.isRequired,
  primary: PropTypes.bool,
};

// Lo que el manager tiene pendiente de hacer. Solo avisa de lo que se puede
// saber con los datos que ya trae la pantalla.
const Attention = ({ data, base }) => {
  const pendingPays = data.pendingPays.total;
  const pendingDebts = data.pendingDebts.length;

  return (
    <Card className="h-100">
      <CardHeader>
        <h5 className="mb-0">Requiere tu atención</h5>
      </CardHeader>
      <CardBody className="pt-0">
        {data.loading || data.pendingPays.loading ? (
          <Loader />
        ) : pendingPays === 0 && pendingDebts === 0 ? (
          <div className="hl-row hl-row--muted">
            <span className="hl-icon-tile hl-icon-tile--sm hl-icon-tile--success" aria-hidden="true">
              <FontAwesomeIcon icon={faCheckCircle} />
            </span>
            <div className="hl-row__body">
              <h6 className="hl-row__title">Todo al día</h6>
              <p className="hl-row__meta mb-0">
                No hay pagos por aprobar ni cuentas por pagar pendientes.
              </p>
            </div>
          </div>
        ) : (
          <>
            {pendingPays > 0 && (
              <AttentionItem
                icon={faClock}
                color="primary"
                title={`${plural(
                  pendingPays,
                  "pago de propietario espera",
                  "pagos de propietarios esperan"
                )} aprobación`}
                detail="Apruébalos o recházalos para que el saldo de cada propiedad quede al día."
                to={sectionHref("collections")}
                actionLabel="Revisar"
                primary
              />
            )}
            {pendingDebts > 0 && (
              <AttentionItem
                icon={faFileInvoiceDollar}
                color="warning"
                title={`${plural(pendingDebts, "cuenta por pagar pendiente", "cuentas por pagar pendientes")}`}
                detail={
                  <>
                    Suman <Money value={data.totalPendingDebts} /> entre todos los proveedores.
                  </>
                }
                to={`${base}/topay`}
                actionLabel="Ver cuentas"
              />
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
};

Attention.propTypes = {
  data: PropTypes.object.isRequired,
  base: PropTypes.string.isRequired,
};

const bankCurrencyLabel = (account) =>
  account?.currency?.iso ? `${account.currency.iso} · ${account.currency.name}` : null;

const AccountRow = ({ account, base }) => {
  const isBank = account.type === "BANK";
  const title = isBank
    ? `${account.bank_name || "Banco"}${
        account.account_number ? ` ···· ${String(account.account_number).slice(-4)}` : ""
      }`
    : "Caja efectivo";

  return (
    <Link to={`${base}/account-movements/${account.id}`} className="hl-row">
      <span className="hl-icon-tile hl-icon-tile--sm hl-icon-tile--neutral" aria-hidden="true">
        <FontAwesomeIcon icon={isBank ? faUniversity : faCashRegister} />
      </span>
      <div className="hl-row__body">
        <h6 className="hl-row__title text-truncate">{title}</h6>
        {bankCurrencyLabel(account) && (
          <span className="hl-row__meta">{bankCurrencyLabel(account)}</span>
        )}
      </div>
      <div className="hl-row__value">
        <Money value={account.balance} className="d-block" />
        <ConvertedMoney
          converted={
            account?.currency
              ? Number(account.balance_bs || 0)
              : undefined
          }
          value={account.balance}
          className="d-block fs--2 text-600"
        />
      </div>
      <ChevronRight size={16} strokeWidth={1.75} className="text-500" aria-hidden="true" />
    </Link>
  );
};

AccountRow.propTypes = {
  account: PropTypes.object.isRequired,
  base: PropTypes.string.isRequired,
};

// Cajas y bancos siempre a la vista: antes los bancos quedaban detrás de un
// "Ver cuentas" que desplegaba otra lista de tarjetas.
const Accounts = ({ accounts, loading, base }) => {
  const listed = accounts.filter((account) => ["CURRENT", "BANK"].includes(account.type));

  return (
    <Card className="h-100">
      <CardHeader className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Cuentas</h5>
        <Link to="/dashboard/banks" className="fs--1 font-weight-semi-bold">
          Administrar bancos
        </Link>
      </CardHeader>
      <CardBody className="pt-0">
        {loading ? (
          <Loader />
        ) : listed.length > 0 ? (
          listed.map((account) => <AccountRow key={account.id} account={account} base={base} />)
        ) : (
          <p className="fs--1 text-600 mb-0">
            Todavía no hay cajas ni cuentas bancarias registradas.
          </p>
        )}
      </CardBody>
    </Card>
  );
};

Accounts.propTypes = {
  accounts: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  base: PropTypes.string.isRequired,
};

const FinanceSummary = ({ data, base, role, condominiumId }) => {
  const isManager = role === "manager";
  const { accounts } = data;
  const available = sumBalances(accounts.filter((account) => account.type !== "ADVANCES"));
  const reserve = sumBalances(accounts.filter((account) => account.type === "ADVANCES"));
  const statCol = isManager ? { md: 4 } : { md: 6 };
  const loadingValue = <span className="text-400">…</span>;
  const hasPendingDebts = data.pendingDebts.length > 0;

  return (
    <>
      <AvailableHero value={available} loading={data.loading} isManager={isManager} base={base} />

      <Row>
        {isManager && (
          <Col {...statCol} className="mb-3">
            <StatCard
              icon={HandCoins}
              color="success"
              label="Por cobrar"
              caption="Lo que deben los propietarios"
              value={
                data.receivableTotal.loading ? (
                  loadingValue
                ) : (
                  <Money value={data.receivableTotal.value} />
                )
              }
              to={sectionHref("collections")}
              linkLabel="Ver cobros"
            />
          </Col>
        )}
        <Col {...statCol} className="mb-3">
          <StatCard
            icon={ReceiptText}
            color={hasPendingDebts ? "danger" : "success"}
            label="Por pagar"
            caption={
              hasPendingDebts
                ? plural(data.pendingDebts.length, "cuenta pendiente", "cuentas pendientes")
                : "¡Al día!"
            }
            value={data.loading ? loadingValue : <Money value={data.totalPendingDebts} />}
            valueClass={hasPendingDebts ? "text-danger" : ""}
            to={isManager ? sectionHref("expenses") : `${base}/topay`}
            linkLabel={isManager ? "Ver gastos" : "Ver cuentas"}
          />
        </Col>
        <Col {...statCol} className="mb-3">
          <StatCard
            icon={PiggyBank}
            color="warning"
            label="Fondo de reserva"
            caption="Ahorros"
            value={data.loading ? loadingValue : <Money value={reserve} />}
                        to={`${base}/reserve-funds`}
            linkLabel="Ver fondos"
          />
        </Col>
      </Row>

      <Row>
        {isManager && (
          <Col lg={7} className="mb-3">
            <Attention data={data} base={base} />
          </Col>
        )}
        <Col lg={isManager ? 5 : 12} className="mb-3">
          <Accounts accounts={accounts} loading={data.loading} base={base} />
        </Col>
      </Row>

      <CondominiumMovements condominiumId={condominiumId} role={role} />
    </>
  );
};

FinanceSummary.propTypes = {
  data: PropTypes.object.isRequired,
  base: PropTypes.string.isRequired,
  role: PropTypes.string,
  condominiumId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default FinanceSummary;
